import React from 'react';

import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react';
import {
  TitleWithDescription,
  LbRadioGroup,
  RadioOption,
  InfoBox,
} from '@infoods/lifebase-ui';

import classes from './PaymentTypeStep.module.scss';
import { ButtonsRow } from '../../components/ButtonsRow/ButtonsRow';
import { setupEntityStore } from '../../../../stores/setupEntityStore';
import {
  BILLING_TYPE,
  BillingType,
} from '../../../../../shared/constants/user-info/billingType';
import { paymentService } from '../../../../../shared/services/paymentService';

type PaymentTypeFormValue = {
  billingType?: BillingType;
  scanIqBillingType?: BillingType;
};

const nutritionOptions: RadioOption<BillingType>[] = [
  {
    label: (
      <TitleWithDescription
        title="Automated Billing Payment"
        description="Customers pay for their subscriptions during self-registration"
        titleFont="title-16-bold"
      />
    ),
    value: BILLING_TYPE.AUTOMATED_BILLING,
  },
  {
    label: (
      <TitleWithDescription
        title="Self-Billing Payment"
        description={
          <span>
            In this case, an entity will take payment from nutrition clients however they
            choose; i.e. cash, check, or other payment processor.
            <br />
            The entity will be automatically debited for each active user on a monthly
            basis.
          </span>
        }
        titleFont="title-16-bold"
      />
    ),
    value: BILLING_TYPE.SELF_BILLING,
  },
];

const scanIqOptions: RadioOption<BillingType>[] = [
  {
    label: (
      <TitleWithDescription
        title="Automated Billing Payment"
        description="Customers pay for their subscriptions during self-registration"
        titleFont="title-16-bold"
      />
    ),
    value: BILLING_TYPE.AUTOMATED_BILLING,
  },
  {
    label: (
      <TitleWithDescription
        title="Self-Billing Payment"
        description="In this case, an entity will take payment from clients however they choose; i.e. cash, check, or other payment processor."
        titleFont="title-16-bold"
      />
    ),
    value: BILLING_TYPE.SELF_BILLING,
  },
];

const PaymentTypeStep = observer(() => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<PaymentTypeFormValue>({
    shouldUnregister: true, // need to remove fields if solution is not suported (hide radio groups)
  });

  const {
    entityId,
    stepperMethods,
    solutionTypeFlags,
    setBillingType,
    setScanIqBillingType,
  } = setupEntityStore;
  const { goToNextStep } = stepperMethods;

  const handleFormSubmit = handleSubmit(async (payload) => {
    if (!entityId) {
      throw new Error('Entity id is not defined');
    }

    const updated = await paymentService.updateBillingType(entityId, payload);

    setBillingType(updated.billingType);
    setScanIqBillingType(updated.scanIqBillingType);
    goToNextStep();
  });

  return (
    <form onSubmit={handleFormSubmit} noValidate>
      <div className={classes.row}>
        {solutionTypeFlags.NUTRITION && (
          <InfoBox title="Payment Type Nutrition">
            <LbRadioGroup
              control={control}
              name="billingType"
              options={nutritionOptions}
              align="start"
              rules={{ required: 'Please select a payment type.' }}
              fullWidth
              defaultValue={BILLING_TYPE.AUTOMATED_BILLING}
            />
          </InfoBox>
        )}
        {solutionTypeFlags.SCAN_IQ && (
          <InfoBox title="Payment Type ScanIQ">
            <LbRadioGroup
              control={control}
              name="scanIqBillingType"
              options={scanIqOptions}
              align="start"
              rules={{ required: 'Please select a payment type.' }}
              fullWidth
              defaultValue={BILLING_TYPE.AUTOMATED_BILLING}
            />
          </InfoBox>
        )}
      </div>
      <ButtonsRow yesDisabled={!isValid || isSubmitting} />
    </form>
  );
});

export { PaymentTypeStep };
